import { createTheme } from '@mui/material/styles';

/**
 * Grid admin theme — matches the marketing website's design language
 * (website/assets/css/styles.css): dark background, cyan accent, sharp
 * ~4px corners, Apple system font, subtle low-contrast borders.
 *
 * Currently scoped to the Analytics page via a local <ThemeProvider>. To roll
 * it out console-wide, wrap the app root in <ThemeProvider theme={gridTheme}>
 * + <CssBaseline /> (see admin/src/App.tsx) and delete the local wrapper.
 */

// Design tokens mirrored from the website :root variables.
export const gridTokens = {
  accent: '#00E5FF',
  accentDark: '#00B8CC',
  accentLight: '#4DFFFF',
  bgPrimary: '#0B0B0D',
  bgSecondary: '#141417',
  bgTertiary: '#1C1C21',
  bgDark: '#050506',
  textPrimary: '#FFFFFF',
  textSecondary: '#C7C7CC',
  textTertiary: '#98989F',
  textMuted: '#8A8A90',
  borderPrimary: '#2A2A30',
  borderSecondary: '#3A3A42',
  danger: '#E02460',
  success: '#22C55E',
  warning: '#EAB308',
  secondary: '#455BFF',
  radius: 4,
} as const;

const fontStack =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const gridTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: gridTokens.accent, dark: gridTokens.accentDark, light: gridTokens.accentLight, contrastText: '#00121A' },
    secondary: { main: gridTokens.secondary },
    error: { main: gridTokens.danger },
    success: { main: gridTokens.success },
    warning: { main: gridTokens.warning },
    background: { default: gridTokens.bgPrimary, paper: gridTokens.bgSecondary },
    text: {
      primary: gridTokens.textPrimary,
      secondary: gridTokens.textSecondary,
      disabled: gridTokens.textMuted,
    },
    divider: gridTokens.borderPrimary,
  },
  shape: { borderRadius: gridTokens.radius },
  typography: {
    fontFamily: fontStack,
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          backgroundColor: gridTokens.bgSecondary,
          backgroundImage: 'none',
          border: `1px solid ${gridTokens.borderPrimary}`,
          borderRadius: gridTokens.radius,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: gridTokens.borderPrimary },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: gridTokens.radius },
        containedPrimary: {
          background: `linear-gradient(135deg, ${gridTokens.accent} 0%, ${gridTokens.accentDark} 100%)`,
          color: '#00121A',
          '&:hover': { filter: 'brightness(1.08)' },
        },
        outlined: { borderColor: gridTokens.borderSecondary },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: gridTokens.radius, fontWeight: 600 },
        outlined: { borderColor: gridTokens.borderSecondary },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          color: gridTokens.textTertiary,
          '&.Mui-selected': { color: gridTokens.accent },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: gridTokens.accent, height: 2 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: gridTokens.borderPrimary },
        head: {
          color: gridTokens.textTertiary,
          fontWeight: 700,
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          backgroundColor: gridTokens.bgTertiary,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: 'rgba(0, 229, 255, 0.04)' } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: gridTokens.radius,
          backgroundColor: gridTokens.bgTertiary,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: gridTokens.borderSecondary },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gridTokens.textTertiary },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: gridTokens.accent },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: gridTokens.bgSecondary,
          backgroundImage: 'none',
          border: `1px solid ${gridTokens.borderPrimary}`,
          borderRadius: gridTokens.radius,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: gridTokens.bgDark,
          border: `1px solid ${gridTokens.borderSecondary}`,
          borderRadius: gridTokens.radius,
          fontSize: 12,
        },
        arrow: { color: gridTokens.bgDark },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked': { color: gridTokens.accent } },
        track: { '.Mui-checked.Mui-checked + &': { backgroundColor: gridTokens.accentDark } },
      },
    },
  },
});
